import React from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';

const CartDrawer = () => {
  const { cart, isCartOpen, setIsCartOpen, updateQuantity, removeFromCart, loading } = useCart();

  const handleQuantityChange = async (productId, currentQty, delta) => {
    const newQty = currentQty + delta;
    if (newQty <= 0) {
      await removeFromCart(productId);
    } else {
      await updateQuantity(productId, newQty);
    }
  };

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent side="right" className="w-full sm:w-[450px] p-0 flex flex-col" data-testid="cart-drawer">
        <SheetHeader className="p-6 border-b border-[#E5E5E5]">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-serif text-2xl font-semibold">Your Cart</SheetTitle>
            <button onClick={() => setIsCartOpen(false)} className="p-1 hover:bg-[#F5F5F5]">
              <X className="w-5 h-5" />
            </button>
          </div>
        </SheetHeader>

        {cart.items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-6" data-testid="empty-cart">
            <ShoppingBag className="w-16 h-16 text-[#D4D4D4] mb-4" />
            <p className="text-[#737373] text-center mb-6">Your cart is empty</p>
            <Button 
              onClick={() => setIsCartOpen(false)}
              className="bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none px-8"
              asChild
            >
              <Link to="/store">Continue Shopping</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {cart.items.map((item) => (
                  <div 
                    key={item.product_id} 
                    className="flex gap-4 pb-6 border-b border-[#E5E5E5] last:border-0"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <div className="w-20 h-24 bg-[#F5F5F5] overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image_url} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#0A0A0A] truncate">{item.name}</h3>
                      <p className="text-sm text-[#737373] mt-1">${item.price.toFixed(2)}</p>
                      
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-[#E5E5E5]">
                          <button
                            onClick={() => handleQuantityChange(item.product_id, item.quantity, -1)}
                            disabled={loading}
                            className="p-2 hover:bg-[#F5F5F5] disabled:opacity-50"
                            data-testid={`decrease-qty-${item.product_id}`}
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-10 text-center text-sm">{item.quantity}</span>
                          <button
                            onClick={() => handleQuantityChange(item.product_id, item.quantity, 1)}
                            disabled={loading || item.quantity >= item.stock}
                            className="p-2 hover:bg-[#F5F5F5] disabled:opacity-50"
                            data-testid={`increase-qty-${item.product_id}`}
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        
                        <button
                          onClick={() => removeFromCart(item.product_id)}
                          disabled={loading}
                          className="p-2 text-[#737373] hover:text-[#0A0A0A] hover:bg-[#F5F5F5]"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#E5E5E5] p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-[#737373]">Subtotal</span>
                <span className="font-serif text-xl font-semibold" data-testid="cart-subtotal">
                  ${cart.subtotal.toFixed(2)}
                </span>
              </div>
              <p className="text-xs text-[#737373]">Shipping calculated at checkout</p>
              
              <Button 
                className="w-full bg-[#0A0A0A] text-white hover:bg-[#262626] rounded-none h-12 btn-lift"
                onClick={() => setIsCartOpen(false)}
                asChild
                data-testid="checkout-btn"
              >
                <Link to="/checkout">Proceed to Checkout</Link>
              </Button>
              
              <Button 
                variant="outline"
                className="w-full rounded-none h-12 border-[#E5E5E5]"
                onClick={() => setIsCartOpen(false)}
                asChild
              >
                <Link to="/store">Continue Shopping</Link>
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
